import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, RefreshCw, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase, FUNCTIONS_URL } from '../lib/supabase';

export default function ConnectTikTok() {
  useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('tiktok_accounts')
      .select('id, username, display_name, avatar_url, followers, connected')
      .order('created_at', { ascending: false });
    setAccounts(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAccounts();
    const params = new URLSearchParams(window.location.search);
    const tiktok = params.get('tiktok');
    if (tiktok === 'success') {
      showToast('TikTok account connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      fetchAccounts();
    } else if (tiktok === 'error') {
      showToast('Failed to connect TikTok account', 'error');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchAccounts]);

  const handleConnect = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token ?? '';
    window.location.href = `${FUNCTIONS_URL}/tiktok-connect?token=${token}`;
  };

  const handleDisconnect = async (id) => {
    if (!window.confirm('Are you sure you want to disconnect this TikTok account?')) return;
    try {
      await supabase.from('tiktok_accounts').delete().eq('id', id);
      setAccounts(prev => prev.filter(a => a.id !== id));
      showToast('Account disconnected');
    } catch {
      showToast('Something went wrong', 'error');
    }
  };

  const formatNum = n => {
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n || '—';
  };

  return (
    <div style={s.container}>
      {toast && (
        <div style={{ ...s.toast, background: toast.type === 'error' ? 'var(--pink)' : 'var(--green)' }}>
          {toast.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
          {toast.msg}
        </div>
      )}

      <div style={s.header}>
        <div>
          <h1 style={s.title}>TikTok Accounts</h1>
          <p style={s.subtitle}>Connect your TikTok accounts and manage content automatically</p>
        </div>
        <button style={s.addBtn} onClick={handleConnect}>
          <Plus size={16} /> Connect TikTok Account
        </button>
      </div>

      <div style={s.infoCard}>
        <div style={s.infoIcon}>ℹ️</div>
        <div>
          <p style={s.infoTitle}>TikTok Developer Account Required</p>
          <p style={s.infoDesc}>
            To use the TikTok API, create an app at <strong>developers.tiktok.com</strong> and add
            <code style={s.code}>TIKTOK_CLIENT_KEY</code> and <code style={s.code}>TIKTOK_CLIENT_SECRET</code> to your
            <code style={s.code}>server/.env</code> file.
          </p>
          <a href="https://developers.tiktok.com" target="_blank" rel="noopener noreferrer" style={s.infoLink}>
            TikTok Developer Portal <ExternalLink size={12} />
          </a>
        </div>
      </div>

      {loading ? (
        <div style={s.loadingRow}>
          <RefreshCw size={20} color="var(--text-muted)" style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading...</span>
        </div>
      ) : accounts.length === 0 ? (
        <div style={s.emptyCard}>
          <div style={s.emptyEmoji}>🎵</div>
          <h3 style={s.emptyTitle}>No TikTok accounts connected</h3>
          <p style={s.emptyDesc}>Connect your TikTok account and start scheduling content.</p>
          <button style={s.emptyBtn} onClick={handleConnect}>
            <Plus size={16} /> Connect TikTok Account
          </button>
        </div>
      ) : (
        <div style={s.accountGrid}>
          {accounts.map(acc => (
            <div key={acc.id} style={s.accountCard}>
              <div style={s.accountTop}>
                <div style={s.accountAvatar}>
                  {acc.avatar_url
                    ? <img src={acc.avatar_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                    : <span>{(acc.username || acc.display_name || 'T')[0].toUpperCase()}</span>
                  }
                </div>
                <div style={s.connectedBadge}>
                  <span style={s.connectedDot} /> Connected
                </div>
              </div>
              <p style={s.accountDisplay}>{acc.display_name || acc.username}</p>
              <p style={s.accountUsername}>@{acc.username}</p>
              <p style={s.accountFollowers}>{formatNum(acc.followers)} followers</p>
              <button style={s.disconnectBtn} onClick={() => handleDisconnect(acc.id)}>
                <Trash2 size={14} /> Disconnect
              </button>
            </div>
          ))}
          <button style={s.addCard} onClick={handleConnect}>
            <Plus size={28} color="var(--text-muted)" />
            <span style={s.addCardText}>Add Account</span>
          </button>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const s = {
  container: { flex: 1, padding: '32px', overflowY: 'auto', position: 'relative' },
  toast: {
    position: 'fixed', top: 20, right: 20,
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '12px 20px', borderRadius: 12,
    color: '#fff', fontSize: 14, fontWeight: 600,
    zIndex: 9999, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontSize: 26, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 },
  subtitle: { fontSize: 14, color: 'var(--text-secondary)' },
  addBtn: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
    background: 'linear-gradient(135deg, var(--accent), var(--pink))',
    color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  infoCard: {
    display: 'flex', gap: 14,
    background: 'rgba(0,212,255,0.07)', border: '1px solid rgba(0,212,255,0.2)',
    borderRadius: 14, padding: '16px 20px', marginBottom: 28,
  },
  infoIcon: { fontSize: 20, flexShrink: 0 },
  infoTitle: { fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 },
  infoDesc: { fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 },
  code: {
    background: 'rgba(108,99,255,0.15)', color: 'var(--accent)',
    padding: '1px 6px', borderRadius: 4, fontSize: 12, fontFamily: 'monospace', margin: '0 2px',
  },
  infoLink: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    marginTop: 8, fontSize: 12, fontWeight: 600, color: 'var(--cyan)', textDecoration: 'none',
  },
  loadingRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '40px 0', justifyContent: 'center' },
  emptyCard: {
    textAlign: 'center', padding: '60px 40px',
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 18, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, maxWidth: 400,
  },
  emptyEmoji: { fontSize: 48 },
  emptyTitle: { fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' },
  emptyDesc: { fontSize: 14, color: 'var(--text-secondary)' },
  emptyBtn: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px',
    background: 'linear-gradient(135deg, var(--accent), var(--pink))',
    color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 8,
  },
  accountGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 },
  accountCard: {
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 16, padding: '20px', display: 'flex', flexDirection: 'column', gap: 4,
  },
  accountTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 },
  accountAvatar: {
    width: 52, height: 52, borderRadius: '50%',
    background: 'linear-gradient(135deg, var(--accent), var(--pink))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 20, fontWeight: 700, color: '#fff', overflow: 'hidden',
  },
  connectedBadge: {
    display: 'flex', alignItems: 'center', gap: 5,
    fontSize: 11, fontWeight: 600, color: 'var(--green)',
    background: 'var(--green-light)', padding: '3px 10px', borderRadius: 20,
  },
  connectedDot: { width: 6, height: 6, borderRadius: '50%', background: 'var(--green)' },
  accountDisplay: { fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' },
  accountUsername: { fontSize: 12, color: 'var(--text-secondary)' },
  accountFollowers: { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginTop: 4 },
  disconnectBtn: {
    display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, padding: '8px 12px',
    background: 'var(--pink-light)', color: 'var(--pink)',
    border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  addCard: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
    background: 'transparent', border: '2px dashed var(--border)',
    borderRadius: 16, padding: '20px', cursor: 'pointer', minHeight: 160,
  },
  addCardText: { fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 },
};
